import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import cx from 'classnames';

import { ContainedButton, OutlinedButton } from '../../../components/modules/Buttons';
import { updateTriggerFolderFetch } from '../../../store/folders/foldersSlice';
import { patchUpdateCollaborativeFolder, postCreateCollaborativeFolder } from '../../../api/foldersApi';
import RolePlate from '../../../components/modules/RolePlate';
import { PATHS, RolesLowerCase, USER, USER_NEW } from '../../../enums';
import Search from '../../../components/modules/Search';
import Avatar from '../../../components/modules/Avatar';
import useDebounce from '../../../helpers/useDebounce';
import Modal from '../../../components/modules/Modal';
import { useAppDispatch } from '../../../store/hooks';
import { NOTIFICATION } from '../../../enums/ENUMS';
import { getUsersByRolesArray } from '../../../api';
import styles from './SidebarModals.module.scss';
import { useHotkeys } from 'react-hotkeys-hook';
import { Portal } from '../../../helpers/bodyPortal';

type TAddCollaborateFolderModal = {
  open: boolean;
  setOpen: (bool: boolean) => void;
  currentUserId: string;
  alreadyCollaborators: USER[];
  folderId: string;
  ownerEmail: string;
};

const EditCollaboratorsModal: React.FC<TAddCollaborateFolderModal> = ({
  open,
  setOpen,
  currentUserId,
  alreadyCollaborators,
  folderId,
  ownerEmail,
}) => {
  const canViewEmails = useCanViewEmails();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const notification = useSnackbar();
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const switchModal = () => {
    setOpen(!open);
  };

  const [collaboratorsToRemove, setCollaboratorsToRemove] = useState<USER[]>([]);

  const onRemoveUserClick = (user: USER) => {
    const arr = [...collaboratorsToRemove, user];
    setCollaboratorsToRemove(arr);
  };

  const [search, setSearch] = useState('');
  const [ignoreUsers, setIgnoreUsers] = useState<string[]>([currentUserId, ...alreadyCollaborators.map((u) => u.id)]);

  const [name, setName] = useState('');
  const [collaborators, setCollaborators] = useState<USER_NEW[]>([]);
  const onClose = () => {
    switchModal();
    setCollaborators([]);
    setName('');
    setSearch('');
    setSearchedUsers([]);
    setIgnoreUsers([currentUserId, ...alreadyCollaborators.map((u) => u.id)]);
    setCollaboratorsToRemove([]);
  };

  const submitNewFolder = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    setLoading(true);
    try {
      let urlPath = `${PATHS.COLLABORATIVE_VIDEOFEEDS_LIST.path}?folderId=${folderId}&parentPage=${PATHS.COLLABORATIVE_FOLDERS.label}`;

      await patchUpdateCollaborativeFolder(
        {
          name,
          collaboratorEmails: collaborators.map((u) => u.user.email),
          collaboratorsToRemove: collaboratorsToRemove.map((u) => u.email),
          urlPath: encodeURI(urlPath),
        },
        folderId,
      );
      dispatch(updateTriggerFolderFetch());
      onClose();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
    } finally {
      setLoading(false);
    }
  };

  const [searchedUsers, setSearchedUsers] = useState<USER_NEW[]>([]);
  const onCollaboratorsSearch = async () => {
    try {
      setLoading(true);
      const res = await getUsersByRolesArray([RolesLowerCase.ANALYST, RolesLowerCase.ORGANIZATION_ADMIN], search, ignoreUsers);
      setSearchedUsers(res.documents);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const debouncedValue = useDebounce(search, 1000);
  useEffect(() => {
    if (search) {
      onCollaboratorsSearch();
    }
  }, [debouncedValue]);

  const onUserSelect = (selectedUser: USER_NEW) => {
    setCollaborators([...collaborators, selectedUser]);
    setSearch('');
    setSearchedUsers([]);
    setIgnoreUsers([...ignoreUsers, selectedUser.user.id]);
  };

  const inputRef = useRef<any>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (inputRef.current && open) {
      const rect = inputRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom,
        left: rect.left,
      });
    }
  }, [open, inputRef, search]); // Ensure it repositions when search changes

  useHotkeys('Enter', () => {
    if (open && !loading) submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalContainer}>
        <div className={styles.modalTitle}>Collaborators</div>
        <form onSubmit={submitNewFolder} className={styles.form}>
          <div className={styles.modalTitle}>Add collaborator</div>
          <Search onChange={(e) => setSearch(e.target.value)} className={cx(styles.searchField, !search ? styles.transparentSearch : '')} />
          <div className={styles.inputRef} ref={inputRef} />
          {search.length > 0 && searchedUsers.length > 0 && (
            <Portal>
              <div className={styles.form}>
                <div className={styles.collaboratorsSearchList} style={{ top: pos.top, left: pos.left }}>
                  {searchedUsers.map((u) => (
                    <div className={styles.searchElement} key={u.user.email} onClick={() => onUserSelect(u)}>
                      <div className={styles.username}>{u?.user.username}</div>
                      {canViewEmails && (<div className={styles.email}>{u.user.email}</div>)}
                    </div>
                  ))}
                </div>
              </div>
            </Portal>
          )}

          {collaborators.length > 0 && (
            <div className={styles.collaboratorsList}>
              {collaborators.map((u) => (
                <div className={styles.user} key={u.user.email}>
                  <Avatar avatar={u.user.avatar_url} name={u.user.email} />{' '}
                  <div className={styles.info}>
                    <div className={styles.username}>{u?.user.username}</div>
                    {canViewEmails && (<div className={styles.email}>{u.user.email} </div>)}
                  </div>
                  <RolePlate className={styles.role} userRole={u?.role?.title} />
                </div>
              ))}
            </div>
          )}

          <div className={styles.modalTitle}>Current collaborators:</div>

          {alreadyCollaborators.length > 0 && (
            <div className={styles.collaboratorsList}>
              {alreadyCollaborators
                .filter((u) => u.id !== currentUserId)
                .filter((u) => !collaboratorsToRemove.find((ur) => ur.email === u.email))
                .map((u) => (
                  <div className={styles.user} key={u.email}>
                    <Avatar avatar={u.avatar_url} name={u.email} />{' '}
                    <div className={styles.info}>
                      <div className={styles.username}>{u?.username}</div>
                      {canViewEmails && (<div className={styles.email}>{u.email}</div>)}
                    </div>
                    {u.email !== ownerEmail ? (
                      <ContainedButton onClick={() => onRemoveUserClick(u)} kind={'red'}>
                        {' '}
                        Remove{' '}
                      </ContainedButton>
                    ) : (
                      <ContainedButton>Creator</ContainedButton>
                    )}
                  </div>
                ))}
            </div>
          )}

          <ContainedButton disabled={loading} className={styles.customBtn} type="submit">
            <div ref={submitButtonRef}>Save changes</div>
          </ContainedButton>
          <OutlinedButton kind="default" disabled={loading} onClick={onClose} type="button">
            Cancel
          </OutlinedButton>
        </form>
      </div>
    </Modal>
  );
};

export default EditCollaboratorsModal;
